import { useMemo } from "react"
import { PropTypes } from "prop-types"
import { Formik, yupToFormErrors } from "formik"
import * as _ from "lodash"

import FormElement from "./FormElement"

export function FormWrapper({
  apiErrorFields,
  children,
  className,
  initialValues,
  name,
  validationSchema,
  onSubmit,
  ...formikProps
}) {
  /**
   * Although we are using Yup, we are passing a "validate" function to Formik instead of the plain "validationSchema"
   * This is to solve an issue where formik doesn't allow us to pass a context into the validate function (https://github.com/jaredpalmer/formik/pull/522)
   * So we are passing the values as a context so we can have access to it later on our customs tests (see example in the validation for multicity dates)
   *
   * @param {object} values values being validated
   */
  const handleValidation = values => {
    return validationSchema
      .validate(values, { abortEarly: false, context: values })
      .then(() => null)
      .catch(err => {
        // Since v2 we need to resolve when there're validation errors instead of reject https://formik.org/docs/migrating-v2#validate
        if (err.name === "ValidationError") {
          return yupToFormErrors(err)
        }
        throw err
      })
  }

  const handleSubmit = (values, { setSubmitting }) => {
    onSubmit(values)
    setSubmitting(false)
  }

  const initialErrorState = useMemo(() => {
    const initialErrors = {}
    const initialTouched = {}

    _.forEach(apiErrorFields, apiErrorField => {
      _.set(initialErrors, apiErrorField, "validationInvalidFieldEntry")
      _.set(initialTouched, apiErrorField, true)
    })

    return {
      initialErrors,
      initialTouched
    }
  }, [apiErrorFields])

  return (
    <Formik
      initialValues={initialValues}
      validate={handleValidation}
      onSubmit={handleSubmit}
      {...formikProps}
      {...initialErrorState}
    >
      {formProps => (
        <FormElement {...formProps} className={className} name={name}>
          {typeof children === "function" ? children(formProps) : children}
        </FormElement>
      )}
    </Formik>
  )
}

FormWrapper.propTypes = {
  apiErrorFields: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.arrayOf(PropTypes.node)])
    .isRequired,
  className: PropTypes.string,
  initialValues: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  name: PropTypes.string,
  validationSchema: PropTypes.object,
  onSubmit: PropTypes.func.isRequired
}

export default FormWrapper
